import { isCvinExpansion } from "../projectGridColumns";
import { currentSubphaseForRow } from "../projectGridColumns";
import { displayPhaseForRow } from "../projectPhaseDisplay";
import {
  buildExpansionSegmentGroups,
  formatExpansionGroupTitle,
  isExpansionParentShellRow,
  segmentSortValue,
  workOrderBaseForRow,
} from "./expansionGroupUtils";
import { normalizeExpansionWorkOrderDisplay } from "./expansionWorkOrderFormat";

export function projectTitleHasCdt(title) {
  return /CDT/i.test(String(title || ""));
}

export function expansionProjectHasCdt(projectRow) {
  if (!projectRow?.cvin_expansion) return false;
  const name = projectRow?.name || projectRow?.project_name || "";
  return projectTitleHasCdt(name);
}

/** CVIN expansion row with a dotted work order (subsegment, not parent shell). */
export function isExpansionSubsegment(row) {
  return isCvinExpansion(row) && !isExpansionParentShellRow(row);
}

export function filterLinkableExpansionSubsegments(rows) {
  return (Array.isArray(rows) ? rows : []).filter(isExpansionSubsegment);
}

export function extractCdtNumberFromTitle(title) {
  const s = String(title || "");
  const m = s.match(/CDT[-\s]?(\d+)/i);
  if (!m) return "";
  return `CDT-${m[1]}`;
}

export function interconnectFieldsToApiBody(fields) {
  if (!fields || typeof fields !== "object") return {};
  return {
    cdtNumber: fields.cdt_number ?? "",
    location: fields.location ?? "",
    gps: fields.gps ?? "",
    locationFinalized: Boolean(fields.location_finalized),
    interconnectingPartner: fields.interconnecting_partner ?? "",
    installPartner: fields.install_partner ?? "",
    associatedSegmentBases: Array.isArray(fields.associated_segment_bases)
      ? fields.associated_segment_bases.map((base) => String(base || "").trim()).filter(Boolean)
      : undefined,
  };
}

/** Display label for a linked expansion subsegment (work order only, two-decimal suffix). */
export function formatInterconnectSegmentLabel(project) {
  if (!project) return "";
  const workOrder = project.workOrder ?? project.work_order ?? "";
  const normalized = normalizeExpansionWorkOrderDisplay(workOrder);
  if (normalized) return normalized;
  const id = project.id;
  return id != null ? `#${id}` : "—";
}

export function formatInterconnectAssociatedSegmentLabel(woBase, segmentGroupsByBase) {
  const base = String(woBase || "").trim();
  if (!base) return "";
  const group = segmentGroupsByBase?.get?.(base);
  if (group?.title) return group.title;
  return formatExpansionGroupTitle(base, "");
}

/** Segment number only (e.g. "12"), without work-order or "Segment" prefix. */
export function formatInterconnectSegmentOnlyLabel(woBase, segmentGroupsByBase) {
  const base = String(woBase || "").trim();
  if (!base) return "";
  const group = segmentGroupsByBase?.get?.(base);
  const seg = group?.segment && String(group.segment).trim();
  return seg || "";
}

/** Numeric comparator for Segment column values (2 before 14). */
export function compareInterconnectSegmentLabels(a, b) {
  const sa = segmentSortValue(a);
  const sb = segmentSortValue(b);
  if (sa !== sb) return sa - sb;
  return String(a || "").localeCompare(String(b || ""), undefined, {
    numeric: true,
    sensitivity: "base",
  });
}

export function buildInterconnectSegmentGroupMap(expansionProjects) {
  const groups = buildExpansionSegmentGroups(expansionProjects || []);
  return new Map(groups.map((group) => [group.woBase || group.key, group]));
}

/** Flat text for grid filter/sort on associated parent segments. */
export function interconnectAssociatedSegmentsText(associatedSegmentBases, segmentGroupsByBase) {
  if (!Array.isArray(associatedSegmentBases) || !associatedSegmentBases.length) return "";
  return associatedSegmentBases
    .map((base) => formatInterconnectAssociatedSegmentLabel(base, segmentGroupsByBase))
    .join(", ");
}

/** Full project row for an interconnect's linked subsegment (from loaded projects map). */
export function resolveLinkedSubsegmentRow(interconnectRow, projectsById) {
  if (!interconnectRow) return null;
  const linkedId = interconnectRow.linkedProjectId ?? interconnectRow.linkedProject?.id;
  if (linkedId == null) return null;
  const fromMap = projectsById?.get?.(Number(linkedId));
  if (fromMap) return fromMap;
  return interconnectRow.linkedProject ?? null;
}

export function linkedSubsegmentSegmentLabel(interconnectRow, segmentGroupsByBase, projectsById) {
  const row = resolveLinkedSubsegmentRow(interconnectRow, projectsById);
  if (!row) return "";
  const base = workOrderBaseForRow(row);
  return formatInterconnectSegmentOnlyLabel(base, segmentGroupsByBase);
}

export function linkedSubsegmentPhaseLabel(interconnectRow, projectsById) {
  const row = resolveLinkedSubsegmentRow(interconnectRow, projectsById);
  if (!row) return "";
  return displayPhaseForRow(row).trim();
}

export function linkedSubsegmentSubphaseLabel(interconnectRow, projectsById) {
  const row = resolveLinkedSubsegmentRow(interconnectRow, projectsById);
  if (!row) return "";
  return currentSubphaseForRow(row) || "";
}
